import { useCallback, useEffect, useRef, useState } from "react";

interface UseImageRetryArgs {
  // Current URL returned by useS3Object (string | null | undefined).
  data: string | null | undefined;
  // refetch from useS3Object's useQuery result. Called on image load
  // error to mint a fresh signed URL.
  refetch: () => Promise<unknown>;
  // Fallback to render until data resolves and after maxRetries is hit.
  fallbackSrc: string;
  // When this value changes (e.g. the underlying S3 key), retry state
  // resets so the new image gets its own retry budget.
  resetKey: string | undefined;
  // How many times to retry before showing the fallback. Default 3.
  maxRetries?: number;
}

interface UseImageRetryReturn {
  src: string;
  handleError: () => Promise<void>;
  // True once at least one retry attempt has been made (and we haven't
  // yet fallen back). Sticky — does NOT clear after the refetch
  // settles. If you need a truly in-flight signal (e.g. a spinner),
  // add a separate state field around the refetch await.
  hasRetried: boolean;
  showFallback: boolean;
}

// Closes the gap that the `957497a3` refetchOnWindowFocus fix doesn't
// reach: when an in-app surface holds an expired signed URL (tab open
// across a deploy, Chrome tab discard/wake, etc.) and the browser hits
// 500 from /_next/image, this hook turns that error into a refetch so
// React Query mints a fresh URL and the component re-renders.
//
// Mirrors the retry-on-error pattern already in S3Image.tsx, extended
// for the Next.js <Image> consumers that don't run through that
// component.
export const useImageRetry = ({
  data,
  refetch,
  fallbackSrc,
  resetKey,
  maxRetries = 3,
}: UseImageRetryArgs): UseImageRetryReturn => {
  const [retryCount, setRetryCount] = useState(0);
  const [showFallback, setShowFallback] = useState(false);
  // Guard against duplicate-fire when a consumer renders the same src
  // in multiple <Image>/<img> elements (e.g. blurred bg + foreground
  // pair in CardTemplateImagePreview / WrapExperienceImage). Without
  // this, one expired URL would burn two retry slots and double-call
  // refetch on near-simultaneous onError events.
  const inFlightRef = useRef(false);

  // Reset when the underlying key changes — different image gets its
  // own retry budget instead of inheriting the previous one's count.
  useEffect(() => {
    setRetryCount(0);
    setShowFallback(false);
    inFlightRef.current = false;
  }, [resetKey]);

  const handleError = useCallback(async () => {
    if (inFlightRef.current) return;
    if (retryCount >= maxRetries) {
      setShowFallback(true);
      return;
    }
    inFlightRef.current = true;
    setRetryCount((c) => c + 1);
    try {
      await refetch();
    } catch (err) {
      console.error("[useImageRetry] refetch failed:", err);
    } finally {
      inFlightRef.current = false;
    }
  }, [retryCount, maxRetries, refetch]);

  const src = showFallback ? fallbackSrc : data || fallbackSrc;

  return {
    src,
    handleError,
    hasRetried: retryCount > 0 && !showFallback,
    showFallback,
  };
};
